import { useState } from "react";

import { css } from "@compiled/react";

import Avatar from "@atlaskit/avatar";
import AvatarGroup from "@atlaskit/avatar-group";
import Link from "@atlaskit/link";
import { token } from "@atlaskit/tokens";

import { ReadStateIndicator } from "./read-state-indicator";
import { NotificationGroup as NotificationGroupType, Actor } from "./types";

const GroupContainerStyle = css({
    marginBlockEnd: token("space.200")
});

const GroupTitleStyle = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: token("color.text.subtlest"),
    marginBlockEnd: token("space.075"),
    marginBlockStart: 0,
    marginInlineStart: token("space.100")
});

const NotificationItemStyle = css({
    "cursor": "pointer",
    "display": "flex",
    "alignItems": "flex-start",
    "flexDirection": "column",
    "padding": token("space.100"),
    "marginBottom": token("space.200"),
    "borderRadius": token("border.radius.100"),
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const NotificationItemContentStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%"
});

const CollapsedGroupItemStyle = css({
    display: "flex",
    alignItems: "center",
    marginBlockStart: token("space.100"),
    paddingInlineStart: token("space.300"),
    width: "100%"
});

const AvatarContainerStyle = css({
    marginInlineEnd: token("space.100")
});

const IconContainerStyle = css({
    marginInlineEnd: token("space.100")
});

const firstNotificationStyle = css({
    margin: 0,
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "14px",
    fontWeight: 500
});

const ClickableTextStyle = css({
    "cursor": "pointer",
    "color": "#172b4d",
    "border": "none",
    "background": "none",
    "padding": 0,
    "font": "inherit",
    "&:hover": {
        textDecoration: "underline"
    },
    "&:focus": {
        outline: `2px solid ${token("color.border.focused")}`
    }
});

interface NotificationGroupProps {
    title: string;
    notificationGroups: NotificationGroupType[];
}

const NotificationGroup = ({
    title,
    notificationGroups
}: NotificationGroupProps) => {
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

    return (
        <div css={GroupContainerStyle}>
            <h5 css={GroupTitleStyle}>{title}</h5>
            {notificationGroups.map((group) => {
                const firstNotification = group.notifications[0];
                const isExpanded = expandedGroupId === group.id;
                const hasAdditionalActors =
                    group.additionalActors && group.additionalActors.length > 0;
                const isCardHovered = hoveredGroupId === group.id;

                if (!firstNotification) return null;

                const isRead = firstNotification.readState === "read";

                return (
                    <div
                        key={group.id}
                        css={NotificationItemStyle}
                        onMouseEnter={() => setHoveredGroupId(group.id)}
                        onMouseLeave={() => setHoveredGroupId(null)}
                        onFocus={() => setHoveredGroupId(group.id)}
                        onBlur={() => setHoveredGroupId(null)}
                    >
                        {/* Main notification content */}
                        <div css={NotificationItemContentStyle}>
                            <div css={AvatarContainerStyle}>
                                <Avatar
                                    size="large"
                                    src={
                                        firstNotification.content.actors?.[0]
                                            ?.avatarUrl
                                    }
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <p css={firstNotificationStyle}>
                                    {firstNotification.content.message}
                                </p>
                                <div
                                    css={css({
                                        display: "flex",
                                        alignItems: "center"
                                    })}
                                >
                                    <div css={IconContainerStyle}>
                                        {/* eslint-disable-next-line no-restricted-syntax */}
                                        <img
                                            src={
                                                firstNotification.content.entity
                                                    ?.icon?.url
                                            }
                                            height={16}
                                            width={16}
                                            alt={
                                                firstNotification.content.entity
                                                    ?.title
                                            }
                                        />
                                    </div>
                                    <Link
                                        appearance="subtle"
                                        href={
                                            firstNotification.content.entity
                                                ?.link?.url
                                        }
                                    >
                                        {
                                            firstNotification.content.entity
                                                ?.title
                                        }
                                    </Link>
                                </div>
                            </div>
                            <ReadStateIndicator
                                isRead={isRead}
                                isCardHovered={isCardHovered}
                                onToggle={() => {
                                    // TODO: Implement read state toggle handler
                                    console.log(
                                        "Toggle read state for notification:",
                                        firstNotification.id
                                    );
                                }}
                            />
                        </div>

                        {/* Collapsed group for additional actors - nested inside the card */}
                        {hasAdditionalActors && (
                            <div css={CollapsedGroupItemStyle}>
                                <div css={AvatarContainerStyle}>
                                    <AvatarGroup
                                        data={group.additionalActors.map(
                                            (actor: Actor) => ({
                                                src: actor.avatarUrl,
                                                name: actor.displayName
                                            })
                                        )}
                                        size="small"
                                    />
                                </div>
                                <div>
                                    <button
                                        css={ClickableTextStyle}
                                        type="button"
                                        onClick={() =>
                                            setExpandedGroupId(
                                                isExpanded ? null : group.id
                                            )
                                        }
                                    >
                                        {`+${group.additionalActors.length} updates from ${group.additionalActors[0]?.displayName} and others`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
export default NotificationGroup;
